"use client";

import { useRef, useEffect } from "react";
import { RotateCw } from "lucide-react";
import { ixToast } from "@shared/src/utils/ixToast";
import { useHostStore } from "@shared/src/stores/useHostStore";

type ErrorType = Error | string | null;

interface ErrorItem {
  error: ErrorType;
  options?: {
    permanent?: boolean;
  };
}

interface ErrorBoundaryProps {
  errors?: ErrorItem | ErrorItem[];
}

export default function ErrorBoundary({ errors = [] }: ErrorBoundaryProps) {
  const hasShownToast = useRef(false);
  const toastIdRef = useRef<string | null>(null);
  const router = useHostStore(state => state.router);

  useEffect(() => {
    // Normalize errors to array
    const errorArray = Array.isArray(errors) ? errors : [errors];

    // Convert errors to Error objects and filter out null/undefined
    const processedErrors = errorArray
      .filter((e): e is ErrorItem & { error: NonNullable<ErrorType> } => e && e.error !== null && e.error !== undefined)
      .map(({ error, options }) => ({
        error: error instanceof Error ? error : new Error(error),
        options,
      }));

    // Deduplicate errors based on message
    const uniqueErrors = processedErrors.reduce((acc, current) => {
      const isDuplicate = acc.some(item => item.error.message === current.error.message);
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, [] as typeof processedErrors);

    const hasErrors = uniqueErrors.length > 0;

    // Show toast if we have errors and haven't shown one yet
    if (hasErrors && !hasShownToast.current) {
      // Format error messages
      const errorMessage =
        uniqueErrors.length === 1 ? (
          uniqueErrors[0].error.message
        ) : (
          <div className="flex flex-col gap-2">
            {uniqueErrors.map(({ error }, index) => (
              <div key={index} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{error.message}</span>
              </div>
            ))}
          </div>
        );

      // Use options from the first error (or default to permanent: true for server errors)
      const options = uniqueErrors[0]?.options ?? { permanent: true };

      // Show single toast with all errors
      const response = ixToast.custom({
        variant: "destructive",
        title: uniqueErrors.length === 1 ? "Error" : `${uniqueErrors.length} Errors Found`,
        description: errorMessage,
        permanent: options.permanent,
        action: (
          <button
            onClick={() => {
              router.refresh();
              hasShownToast.current = false;
            }}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-destructive text-destructive-foreground hover:bg-destructive/90 h-8 px-3"
          >
            <RotateCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        ),
        onOpenChange: open => {
          if (!open) {
            hasShownToast.current = false;
            toastIdRef.current = null;
          }
        },
      });

      toastIdRef.current = response.id;
      hasShownToast.current = true;
    }

    // Reset state if no errors
    if (!hasErrors) {
      hasShownToast.current = false;
      toastIdRef.current = null;
    }
  }, [errors, router]);

  return null;
}
